import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ShoppingCart, ExternalLink, Star, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

// Book cover from Amazon
const BOOK_COVER = "https://m.media-amazon.com/images/I/81AOcWnQWrL._SY342_.jpg";
const AMAZON_LINK = "https://www.amazon.com/dp/B0GWSTR45B";

export default function BookSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      id="book"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 bg-card/30 relative overflow-hidden"
      data-testid="book-section"
    >
      {/* Background elements */}
      <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-96 h-96 gradient-blob" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Book Cover */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative flex justify-center"
          >
            <div className="relative max-w-sm">
              {/* Glowing background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-accent/40 blur-3xl transform translate-y-8 rounded-full" />
              
              {/* Book cover image */}
              <div className="relative frame-border p-2">
                <img
                  src={BOOK_COVER}
                  alt="Pivot To Power: Black America's Guide to Thriving in the AI Economy"
                  className="w-full rounded-lg shadow-2xl"
                  data-testid="book-cover-image"
                />
              </div>

              {/* Badge */}
              <div className="absolute -top-4 -right-4 bg-primary text-primary-foreground px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wider shadow-lg">
                Available Now
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="text-xs font-['Orbitron'] uppercase tracking-[0.2em] text-primary mb-4 block">
              New Book Release
            </span>
            <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-4">
              Pivot To
              <br />
              <span className="gradient-text">Power</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-2 font-semibold">
              Black America's Guide to Thriving in the AI Economy
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              By Shea Richburg & Raulvin Coke
            </p>
            
            <div className="space-y-4 text-base text-muted-foreground mb-8 leading-relaxed">
              <p>
                They told us to get the degree. Work twice as hard. Keep your head down. Wait your turn.
                <span className="text-primary font-semibold"> The AI economy just set that contract on fire.</span>
              </p>
              <p>
                Right now, artificial intelligence is quietly eliminating the jobs we were told were safe. 
                This is not a technology problem. It is a power problem.
              </p>
              <p className="text-foreground font-semibold italic">
                "Your Blackness is not a liability in the AI economy. It is your greatest competitive advantage."
              </p>
            </div>

            {/* Book details */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <BookOpen size={16} className="text-primary" />
                <span>114 pages</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Star size={16} className="text-primary" />
                <span>Kindle Edition</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={AMAZON_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button
                  className="w-full sm:w-auto bg-primary text-primary-foreground rounded-full px-8 h-14 font-bold uppercase tracking-wider text-base hover:bg-primary/90 transition-all btn-glow"
                  data-testid="book-buy-btn"
                >
                  <ShoppingCart size={20} className="mr-3" />
                  Buy on Amazon - $8.00
                </Button>
              </a>
              <a
                href={AMAZON_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button
                  variant="outline"
                  className="w-full sm:w-auto bg-transparent border-2 border-primary/50 text-primary rounded-full px-8 h-14 font-bold uppercase tracking-wider text-base hover:bg-primary/10 hover:border-primary transition-all"
                  data-testid="book-preview-btn"
                >
                  <ExternalLink size={20} className="mr-3" />
                  Read Sample
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
